import { api } from '../api.js';
import { navigate } from '../router.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { formatDateTime as fmtDate } from '../../../../src/shared/format-time.js';
import {
  NEWS_BODY_MAX_LENGTH,
  NEWS_TITLE_MAX_LENGTH,
  renderNewsHtml,
} from '../../../../src/shared/news-content.js';

let _listBound = false;

async function loadList() {
  const body = document.getElementById('admin-news-body');
  const statusEl = document.getElementById('admin-news-status');
  if (!body) return;

  statusEl.textContent = 'Loading...';
  body.innerHTML = '';

  try {
    const res = await api.get('/api/admin/news');
    const items = res.items || [];

    if (!items.length) {
      statusEl.textContent = 'No news items yet.';
      return;
    }

    statusEl.textContent = `${items.length} item${items.length === 1 ? '' : 's'}`;

    body.innerHTML = items.map(r => {
      const status = r.published_at
        ? '<span style="color:var(--accent)">Published</span>'
        : '<span style="opacity:0.5">Draft</span>';
      return `
      <tr class="contact-row-clickable" data-id="${esc(String(r.id))}">
        <td style="white-space:nowrap">${esc(fmtDate(r.created_at))}</td>
        <td>${esc(r.title)}</td>
        <td>${status}</td>
      </tr>`;
    }).join('');

    body.querySelectorAll('.contact-row-clickable').forEach(tr => {
      tr.addEventListener('click', () => {
        navigate(`/admin/news/${tr.dataset.id}`);
      });
    });
  } catch {
    statusEl.textContent = 'Failed to load news items.';
  }
}

async function loadDetail(id) {
  const content = document.getElementById('admin-news-detail-content');
  if (!content) return;

  const isNew = id === 'new';
  let item = { title: '', body: '', published_at: null };

  if (!isNew) {
    content.innerHTML = '<div class="admin-status">Loading...</div>';
    try {
      item = await api.get(`/api/admin/news/${id}`);
    } catch {
      content.innerHTML = '<div class="admin-status">Failed to load news item.</div>';
      return;
    }
  }

  const publishLabel = item.published_at ? 'Unpublish' : 'Publish';
  const deleteBtn = isNew ? '' : `
    <button class="btn btn-mode" id="news-detail-delete" style="background:#b3261e;color:#fff">Delete</button>`;

  content.innerHTML = `
    <div class="card admin-card">
      <div class="card-label">${isNew ? 'NEW POST' : 'EDIT POST'}</div>
      <label style="display:block;margin-bottom:12px">
        <span style="display:block;margin-bottom:4px;font-weight:600">Title</span>
        <input type="text" id="news-detail-title" class="settings-input" value="${esc(item.title)}" maxlength="${NEWS_TITLE_MAX_LENGTH}" style="width:100%;box-sizing:border-box">
      </label>
      <label for="news-detail-body" style="display:block;margin-bottom:4px;font-weight:600">Body (limited HTML)</label>
      <textarea id="news-detail-body" class="settings-input" maxlength="${NEWS_BODY_MAX_LENGTH}" style="width:100%;box-sizing:border-box;min-height:300px;font-family:monospace;font-size:13px">${esc(item.body || '')}</textarea>
      <div style="margin-top:16px;margin-bottom:8px;font-weight:600">Preview</div>
      <div id="news-detail-preview" style="border:1px solid var(--border, #333);border-radius:8px;padding:16px;min-height:60px;white-space:pre-wrap;overflow-wrap:anywhere"></div>
    </div>
    <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:12px">
      <button class="btn btn-mode btn-primary" id="news-detail-save">Save</button>
      ${isNew ? '' : `<button class="btn btn-mode" id="news-detail-publish">${publishLabel}</button>`}
      ${deleteBtn}
      <span id="news-detail-status" class="admin-status" role="status" aria-live="polite" style="margin:0"></span>
    </div>
  `;

  // Live preview
  const textarea = document.getElementById('news-detail-body');
  const preview = document.getElementById('news-detail-preview');
  renderNewsHtml(preview, item.body || '');
  textarea.addEventListener('input', () => {
    renderNewsHtml(preview, textarea.value);
  });

  // Save
  document.getElementById('news-detail-save').addEventListener('click', async () => {
    const statusEl = document.getElementById('news-detail-status');
    const title = document.getElementById('news-detail-title').value.trim();
    const body = textarea.value;
    if (!title) { statusEl.textContent = 'Title is required.'; return; }
    statusEl.textContent = 'Saving...';
    try {
      if (isNew) {
        await api.post('/api/admin/news', { title, body });
        navigate('/admin/news');
        return;
      }
      await api.patch(`/api/admin/news/${id}`, { title, body });
      statusEl.textContent = 'Saved';
      statusEl.style.color = 'var(--accent)';
      setTimeout(() => { statusEl.textContent = ''; }, 3000);
    } catch {
      statusEl.textContent = 'Failed to save.';
      statusEl.style.color = '#f06060';
    }
  });

  // Publish / Unpublish
  document.getElementById('news-detail-publish')?.addEventListener('click', async () => {
    const statusEl = document.getElementById('news-detail-status');
    const newPub = item.published_at ? null : new Date().toISOString();
    statusEl.textContent = newPub ? 'Publishing...' : 'Unpublishing...';
    try {
      await api.patch(`/api/admin/news/${id}`, { published_at: newPub });
      item.published_at = newPub;
      loadDetail(id);
    } catch {
      statusEl.textContent = 'Failed';
      statusEl.style.color = '#f06060';
    }
  });

  // Delete
  document.getElementById('news-detail-delete')?.addEventListener('click', async () => {
    if (!confirm('Delete this news item?')) return;
    const statusEl = document.getElementById('news-detail-status');
    statusEl.textContent = 'Deleting...';
    try {
      await api.delete(`/api/admin/news/${id}`);
      navigate('/admin/news');
    } catch {
      statusEl.textContent = 'Failed to delete.';
      statusEl.style.color = '#f06060';
    }
  });
}

// ── Exports ──────────────────────────────────────────────

export function renderList() {
  if (!_listBound) {
    _listBound = true;
    document.getElementById('admin-news-create')?.addEventListener('click', () => {
      navigate('/admin/news/new');
    });
  }
  loadList();
}

export function renderDetail(params) {
  return loadDetail(params.id);
}
